/**
 * HeroCanvas — §8 Hero v2.
 *
 * The Apple-style scroll-driven hero. Pin/sticky + scrub + storyboard dwell —
 * with three render modes:
 *
 *   • 'procedural' (default) — zero assets; the canvas paints an app being
 *     assembled (grid → window wireframe → panels/chart/chat → accent
 *     ignition → settle) purely from scroll progress.
 *   • 'frames' — 150 pre-extracted JPGs (see scripts/extract-frames.mjs).
 *   • 'video'  — seeks a muted <video> by progress (kept for completeness).
 *
 * Non-negotiable rules (owner decision 2026-07-18 — the Apple pattern; this
 * SUPERSEDES every earlier scroll-lock design, desktop's included):
 *   1. Pin the wrapper INSIDE the scroll container; never animate window scroll.
 *   2. Scrub a proxy — every visual derives from a single progress number.
 *   3. NO input interception, ever, on any input class: no wheel swallowing, no
 *      scroll clamping, no touch listeners, no snap points. Native momentum always
 *      wins. Reading time comes from scroll DISTANCE, not from stopping the user.
 *   4. The pacing lives in the STORYBOARD: visual progress is clamp01(raw /
 *      ANIMATION_END), so the sequence completes at 62% of the runway and the
 *      settled final frame + headline + CTAs dwell for the remaining ~38%
 *      (~76vh on desktop's 300vh pinned container, ~99vh on touch's 360vh native
 *      sticky runway). A fling that sails through is accepted — Apple's trade.
 *      The end of the dwell (raw 0.72→1) carries the exit push-in: on portrait
 *      screens the scene zooms until the app window covers the visible strip, so
 *      the hero is LEFT the way desktop ends — covered.
 *   5. Scrub stays hard-linked (scrub:true) everywhere — a numeric scrub lagged
 *      the unpin boundary → clip. Desktop's cinematic feel comes from Lenis
 *      lerping the scroll itself, never from blocking it.
 *   6. Reduced motion / CDN failure / no canvas ⇒ static final frame. Always.
 */
import { useEffect, useRef, useState } from 'react';
import { loadGsap, type ScrollTriggerSelf } from '../lib/cdn';
import HeroPrompt from './HeroPrompt';

export type HeroMode = 'procedural' | 'frames' | 'video';

export interface HeroCanvasProps {
  mode?: HeroMode;
  /** total scroll distance driving the animation (container height) */
  scrollHeight?: string;
  /** frames mode */
  framePathPattern?: string; // e.g. '/frames/frame_####.jpg'
  frameCount?: number;
  frameStart?: number;
  framePadLength?: number;
  /** video mode */
  videoSrc?: string;
  /** copy */
  eyebrow?: string;
  headline?: string;
  headlineAccent?: string;
  introSub?: string;
  finalHeadline?: string;
  finalSub?: string;
  ctaPrimary?: { label: string; href: string };
  ctaSecondary?: { label: string; href: string };
  onPhase?: (phase: 'intro' | 'mid' | 'final') => void;
}

/* ── deterministic PRNG so the particle field is identical every visit ──── */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));
/** progress p remapped to 0..1 inside [a,b], eased smoothstep */
function seg(p: number, a: number, b: number): number {
  const t = clamp01((p - a) / (b - a));
  return t * t * (3 - 2 * t);
}

/* internal render resolution (DPR-capped), cover-fitted to the viewport */
const W = 1920;
const H = 1080;

/* per-theme draw palettes — cyan accent on graphite (dark) or paper (light).
   Read at draw time so a theme toggle repaints correctly (see 'helix:theme'). */
const PALETTES = {
  dark: {
    accent: '#22D3EE',
    a: '34,211,238', // accent rgb triplet for rgba() strings
    bgTop: '#0B0D12', bgBot: '#070910',
    grid: 'rgba(148,163,184,0.5)',
    particle: '#8B93A7',
    frame: 'rgba(148,163,184,0.7)',
    glass: 'rgba(17,20,28,0.92)',
    divider: 'rgba(148,163,184,0.3)',
    panel: 'rgba(7,9,16,0.9)',
    row: 'rgba(148,163,184,0.35)',
    barTop: 'rgba(148,163,184,0.85)', barBot: 'rgba(148,163,184,0.15)',
    bubble: 'rgba(148,163,184,0.14)',
    vig: '7,9,16',
  },
  light: {
    accent: '#0891B2',
    a: '8,145,178',
    bgTop: '#FFFFFF', bgBot: '#EAF0F6',
    grid: 'rgba(100,116,139,0.5)',
    particle: '#64748B',
    frame: 'rgba(51,65,85,0.7)',
    glass: 'rgba(255,255,255,0.92)',
    divider: 'rgba(100,116,139,0.3)',
    panel: 'rgba(234,240,246,0.9)',
    row: 'rgba(100,116,139,0.4)',
    barTop: 'rgba(100,116,139,0.85)', barBot: 'rgba(100,116,139,0.15)',
    bubble: 'rgba(100,116,139,0.14)',
    vig: '148,163,184',
  },
} as const;

function activePalette() {
  return document.documentElement.dataset.theme === 'dark' ? PALETTES.dark : PALETTES.light;
}

/* ── procedural storyboard (§8.3) ────────────────────────────────────────
   0.00–0.18  perspective grid rises from the floor, particles drift up
   0.12–0.40  app window wireframe draws itself on (dashed stroke reveal)
   0.34–0.66  UI assembles: sidebar, 5 chart bars (staggered), 3 chat bubbles
   0.62–0.84  scanline sweep + accent ignition (glow pulses, seams light)
   0.84–1.00  settle: glow decays to a steady ember, vignette deepens        */
function drawProcedural(
  ctx: CanvasRenderingContext2D, p: number, boxW: number, boxH: number, exitT = 0,
): void {
  const pal = activePalette();
  ctx.clearRect(0, 0, W, H);

  // backdrop
  const bgGrad = ctx.createLinearGradient(0, 0, 0, H);
  bgGrad.addColorStop(0, pal.bgTop);
  bgGrad.addColorStop(1, pal.bgBot);
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, W, H);

  // ── fit-to-screen ── the canvas is 1920×1080 painted with object-fit:cover,
  // so portrait crops the sides; shrink the foreground scene (about centre) until
  // the app window fits the cover-visible strip — frames must NOT overflow the
  // phone's sides. boxW/boxH are the canvas's ACTUAL rendered CSS box (what cover
  // measures against) — NOT window.innerWidth/Height. That was the S25U bug
  // (2026-07-09): the wrapper is 100lvh (URL-bar-HIDDEN height) but innerHeight is
  // the bar-VISIBLE height, so on a real phone the two disagreed → cover cropped
  // harder than the fit assumed → the window drew wider than the screen and its
  // sides spilled off (DevTools has no dynamic bar, so it matched and hid the bug).
  // fit === 1 on desktop/landscape.
  const vw = boxW || W;
  const vh = boxH || H;
  const coverScale = Math.max(vw / W, vh / H);
  const fit = Math.min(1, ((vw / coverScale) * 0.94) / 1180, ((vh / coverScale) * 0.92) / 680);
  // Exit push-in (RESTORED 2026-07-18, owner request — originally 275349b, lost as
  // collateral in the cover-parity churn, retimed from the settle to the dwell):
  // as the hero is left, the scene zooms from the fitted size until the app window
  // COVERS the visible strip, so phones end the way desktop ends. The 1.1 overshoot
  // absorbs the window's -20px vertical offset (corners land off-screen). Vector
  // redraw per frame keeps it crisp. fit === 1 (desktop/landscape) is untouched —
  // desktop's final frame already covers the screen.
  let sceneScale = fit;
  if (fit < 1 && exitT > 0) {
    const coverFit = Math.max((vw / coverScale) / 1180, (vh / coverScale) / 680) * 1.1;
    if (coverFit > fit) sceneScale = fit + (coverFit - fit) * exitT;
  }
  ctx.save();
  if (sceneScale !== 1) {
    ctx.translate(W / 2, H / 2);
    ctx.scale(sceneScale, sceneScale);
    ctx.translate(-W / 2, -H / 2);
  }

  const horizon = H * 0.62;

  /* 1 — perspective grid */
  const gridIn = seg(p, 0.0, 0.18);
  const gridOut = 1 - seg(p, 0.55, 0.75); // grid fades as the app takes over
  const gridA = gridIn * gridOut;
  if (gridA > 0.01) {
    ctx.save();
    ctx.globalAlpha = gridA * 0.5;
    ctx.strokeStyle = pal.grid;
    ctx.lineWidth = 1;
    // converging verticals
    for (let i = -10; i <= 10; i++) {
      const xTop = W / 2 + i * 260;
      const xBot = W / 2 + i * 90;
      ctx.beginPath();
      ctx.moveTo(xBot, horizon);
      ctx.lineTo(xTop, H + 80);
      ctx.stroke();
    }
    // horizontals rising toward the horizon
    for (let r = 0; r < 9; r++) {
      const t = r / 8;
      const y = horizon + (H - horizon) * Math.pow(t, 1.8) * gridIn;
      ctx.globalAlpha = gridA * 0.45 * (0.25 + 0.75 * t);
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(W, y);
      ctx.stroke();
    }
    // horizon line warms slightly
    ctx.globalAlpha = gridA * 0.7;
    ctx.strokeStyle = `rgba(${pal.a},${0.18 * gridA})`;
    ctx.beginPath();
    ctx.moveTo(0, horizon);
    ctx.lineTo(W, horizon);
    ctx.stroke();
    ctx.restore();
  }

  /* drifting spark particles (seeded, scroll-parallax) */
  const rand = mulberry32(1337);
  const partA = seg(p, 0.02, 0.2) * (1 - seg(p, 0.8, 0.97));
  if (partA > 0.01) {
    ctx.save();
    for (let i = 0; i < 70; i++) {
      const px = rand() * W;
      const baseY = rand() * H;
      const speed = 60 + rand() * 220;
      const size = 0.8 + rand() * 1.8;
      const py = ((baseY - p * speed * 4) % H + H) % H;
      const tw = 0.35 + 0.65 * rand();
      ctx.globalAlpha = partA * 0.5 * tw;
      ctx.fillStyle = rand() > 0.82 ? pal.accent : pal.particle;
      ctx.fillRect(px, py, size, size);
    }
    ctx.restore();
  }

  /* 2 — app window wireframe */
  const winW = 1180;
  const winH = 680;
  const wx = (W - winW) / 2;
  const wy = (H - winH) / 2 - 20;
  const drawOn = seg(p, 0.12, 0.4);
  const rise = (1 - seg(p, 0.12, 0.34)) * 60; // window floats up into place

  if (drawOn > 0.005) {
    ctx.save();
    ctx.translate(0, rise);

    // frame, stroke-revealed via line dash
    const perim = 2 * (winW + winH);
    ctx.strokeStyle = pal.frame;
    ctx.lineWidth = 2;
    ctx.setLineDash([perim]);
    ctx.lineDashOffset = perim * (1 - drawOn);
    roundRectPath(ctx, wx, wy, winW, winH, 18);
    ctx.stroke();
    ctx.setLineDash([]);

    // interior appears once the outline is mostly drawn
    const ui = seg(p, 0.3, 0.62);
    if (ui > 0.01) {
      // glass fill
      ctx.globalAlpha = ui * 0.9;
      ctx.fillStyle = pal.glass;
      roundRectPath(ctx, wx, wy, winW, winH, 18);
      ctx.fill();
      // re-stroke frame above the fill
      ctx.globalAlpha = 1;
      ctx.strokeStyle = pal.frame;
      roundRectPath(ctx, wx, wy, winW, winH, 18);
      ctx.stroke();

      // titlebar + traffic lights
      ctx.globalAlpha = ui;
      ctx.strokeStyle = pal.divider;
      ctx.beginPath();
      ctx.moveTo(wx, wy + 52);
      ctx.lineTo(wx + winW, wy + 52);
      ctx.stroke();
      const lights = ['#FF5F57', '#FEBC2E', '#28C840'];
      lights.forEach((c, i) => {
        ctx.fillStyle = c;
        ctx.beginPath();
        ctx.arc(wx + 28 + i * 26, wy + 26, 6.5, 0, Math.PI * 2);
        ctx.fill();
      });

      /* 3 — assembly: sidebar, chart, chat — each staggers in */
      const innerX = wx + 24;
      const innerY = wy + 76;

      // sidebar
      const sbT = seg(p, 0.36, 0.48);
      if (sbT > 0.01) {
        ctx.globalAlpha = ui * sbT;
        ctx.fillStyle = pal.panel;
        roundRectPath(ctx, innerX, innerY + (1 - sbT) * 24, 220, winH - 124, 12);
        ctx.fill();
        ctx.fillStyle = pal.row;
        for (let i = 0; i < 6; i++) {
          const rowT = seg(p, 0.38 + i * 0.015, 0.5 + i * 0.015);
          ctx.globalAlpha = ui * rowT * 0.9;
          roundRectPath(ctx, innerX + 16, innerY + 22 + i * 40, 150 * (0.55 + 0.45 * ((i * 37) % 10) / 10), 10, 5);
          ctx.fill();
        }
      }

      // chart panel — 5 bars staggered
      const chX = innerX + 244;
      const chY = innerY;
      const chW = winW - 48 - 244;
      const chH = 320;
      const chT = seg(p, 0.4, 0.52);
      if (chT > 0.01) {
        ctx.globalAlpha = ui * chT;
        ctx.fillStyle = pal.panel;
        roundRectPath(ctx, chX, chY + (1 - chT) * 24, chW, chH, 12);
        ctx.fill();
        const heights = [0.42, 0.68, 0.5, 0.86, 0.62];
        const barW = 64;
        const gap = (chW - 60 - heights.length * barW) / (heights.length - 1);
        heights.forEach((hF, i) => {
          const bT = seg(p, 0.44 + i * 0.022, 0.56 + i * 0.022);
          if (bT <= 0.01) return;
          const bH = (chH - 80) * hF * bT;
          const bx = chX + 30 + i * (barW + gap);
          const by = chY + chH - 30 - bH;
          const g = ctx.createLinearGradient(0, by, 0, by + bH);
          g.addColorStop(0, i === 3 ? pal.accent : pal.barTop);
          g.addColorStop(1, pal.barBot);
          ctx.globalAlpha = ui * bT;
          ctx.fillStyle = g;
          roundRectPath(ctx, bx, by, barW, bH, 6);
          ctx.fill();
        });
      }

      // chat panel — 3 bubbles
      const cpY = chY + chH + 20;
      const cpH = winH - 124 - chH - 20;
      const cpT = seg(p, 0.48, 0.6);
      if (cpT > 0.01) {
        ctx.globalAlpha = ui * cpT;
        ctx.fillStyle = pal.panel;
        roundRectPath(ctx, chX, cpY + (1 - cpT) * 24, chW, cpH, 12);
        ctx.fill();
        const bubbles: Array<[number, number, boolean]> = [
          [0.5, 0, false],
          [0.62, 1, true],
          [0.44, 2, false],
        ];
        bubbles.forEach(([wF, i, mine]) => {
          const bT = seg(p, 0.52 + i * 0.03, 0.62 + i * 0.03);
          if (bT <= 0.01) return;
          const bw = chW * 0.5 * wF + 120;
          const bx = mine ? chX + chW - 24 - bw : chX + 24;
          const by = cpY + 20 + i * 52;
          ctx.globalAlpha = ui * bT;
          ctx.fillStyle = mine ? `rgba(${pal.a},0.16)` : pal.bubble;
          roundRectPath(ctx, bx, by, bw, 34, 10);
          ctx.fill();
          if (mine) {
            ctx.strokeStyle = `rgba(${pal.a},0.4)`;
            ctx.lineWidth = 1;
            roundRectPath(ctx, bx, by, bw, 34, 10);
            ctx.stroke();
          }
        });
      }
    }

    /* 4 — scanline sweep + ignition */
    const scan = seg(p, 0.62, 0.78);
    if (scan > 0.01 && scan < 0.999) {
      const sy = wy + winH * scan;
      const g = ctx.createLinearGradient(0, sy - 60, 0, sy + 60);
      g.addColorStop(0, `rgba(${pal.a},0)`);
      g.addColorStop(0.5, `rgba(${pal.a},${0.22 * Math.sin(scan * Math.PI)})`);
      g.addColorStop(1, `rgba(${pal.a},0)`);
      ctx.globalAlpha = 1;
      ctx.fillStyle = g;
      ctx.fillRect(wx, sy - 60, winW, 120);
    }

    const ignite = seg(p, 0.66, 0.84);
    const settle = seg(p, 0.84, 1.0);
    const glow = ignite * (1 - settle * 0.55); // flare then ember
    if (glow > 0.01) {
      ctx.globalAlpha = 1;
      ctx.shadowColor = pal.accent;
      ctx.shadowBlur = 24 * glow; // §8 cap: never above 24 — GPU budget
      ctx.strokeStyle = `rgba(${pal.a},${0.55 + 0.45 * glow})`;
      ctx.lineWidth = 2.5;
      roundRectPath(ctx, wx, wy, winW, winH, 18);
      ctx.stroke();
      ctx.shadowBlur = 0;
      // weld seam across the titlebar
      ctx.strokeStyle = `rgba(${pal.a},${0.5 * glow})`;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(wx, wy + 52);
      ctx.lineTo(wx + winW * Math.min(1, ignite * 1.2), wy + 52);
      ctx.stroke();
    }

    ctx.restore();
  }

  ctx.restore(); // end fit-to-screen zoom

  /* 5 — settle vignette */
  const vig = 0.25 + 0.3 * seg(p, 0.8, 1.0);
  const rg = ctx.createRadialGradient(W / 2, H / 2, H * 0.3, W / 2, H / 2, H * 0.95);
  rg.addColorStop(0, `rgba(${pal.vig},0)`);
  rg.addColorStop(1, `rgba(${pal.vig},${vig})`);
  ctx.globalAlpha = 1;
  ctx.fillStyle = rg;
  ctx.fillRect(0, 0, W, H);
}

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/* cover-fit an image/video frame onto the 1920×1080 canvas */
function drawCover(
  ctx: CanvasRenderingContext2D,
  src: HTMLImageElement | HTMLVideoElement,
  sw: number, sh: number,
): void {
  if (!sw || !sh) return;
  const scale = Math.max(W / sw, H / sh);
  const dw = sw * scale;
  const dh = sh * scale;
  ctx.clearRect(0, 0, W, H);
  ctx.drawImage(src, (W - dw) / 2, (H - dh) / 2, dw, dh);
}

export default function HeroCanvas({
  mode = 'procedural',
  scrollHeight,
  framePathPattern = '/frames/frame_####.jpg',
  frameCount = 150,
  frameStart = 1,
  framePadLength = 4,
  videoSrc,
  eyebrow = 'PIIXEL // BUILD ENGINE',
  headline = 'Describe it.',
  headlineAccent = 'Ship it.',
  introSub = 'The AI app builder that validates every change before you pay for it.',
  finalHeadline = 'Built here.\nYours everywhere.',
  finalSub = 'Every run is versioned. Export the complete codebase whenever you want. Your work stays yours.',
  ctaPrimary = { label: 'Start building', href: '/signup' },
  ctaSecondary = { label: 'See it in action', href: '#features' },
  onPhase,
}: HeroCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const phase1Ref = useRef<HTMLDivElement>(null);
  const phase3Ref = useRef<HTMLDivElement>(null);
  const hintRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(false); // video mode only
  const [fallback, setFallback] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    const wrapper = wrapperRef.current;
    const canvas = canvasRef.current;
    if (!container || !wrapper || !canvas) return;

    // Capability, not hover policy: a stylus-capable phone may report hover even while
    // the user scrolls with a finger. Touch-capable devices use the native sticky runway.
    const touchCapable = navigator.maxTouchPoints > 0
      || window.matchMedia('(pointer: coarse)').matches;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    container.dataset.nativeTouch = touchCapable ? 'true' : 'false';
    // Apple-pattern geometry (owner decision 2026-07-18): the sequence completes at
    // ANIMATION_END of the runway and the settled final frame + copy + CTAs dwell for
    // the rest — reading time is scroll distance, never input interception. Touch:
    // 360vh (260vh sticky travel → sequence over ~161vh, dwell ~99vh — the S25U-
    // verified pacing). Desktop: 300vh (200vh pinned travel → sequence over ~124vh,
    // dwell ~76vh; was 200vh + a hard lock, both now gone).
    container.style.height = scrollHeight ?? (touchCapable ? '360vh' : '300vh');
    const ANIMATION_END = 0.62;

    const ctx = canvas.getContext('2d');
    if (!ctx) { setFallback(true); return; }

    // internal resolution: 1920×1080, DPR-capped at 2 (§8.5 GPU budget)
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);

    // The canvas's rendered CSS box = what object-fit:cover crops against (the
    // 100lvh wrapper). Measure it (stable across URL-bar animation, unlike
    // window.innerHeight) and feed it to drawProcedural's fit math so the app
    // window can never overflow the phone's sides. Re-measure only on a real
    // resize/rotation, never per frame (getBoundingClientRect forces layout).
    let boxW = W, boxH = H;
    const measureBox = (): boolean => {
      const r = canvas.getBoundingClientRect();
      const nw = Math.round(r.width) || W;
      const nh = Math.round(r.height) || H;
      if (nw === boxW && nh === boxH) return false;
      boxW = nw; boxH = nh;
      return true;
    };
    measureBox();

    /* ── frame sources ── */
    const images: HTMLImageElement[] = [];
    if (mode === 'frames') {
      for (let i = 0; i < frameCount; i++) {
        const img = new Image();
        const n = String(frameStart + i).padStart(framePadLength, '0');
        img.src = framePathPattern.replace('####', n);
        images.push(img);
      }
      // NON-BLOCKING preloader (guide rule): reveal instantly, frames pop in.
      // Once all settle, force one redraw at the current progress so static
      // paths (reduced-motion, CDN fallback) don't stay blank.
      void Promise.all(
        images.map((img) => new Promise<void>((res) => {
          if (img.complete) return res();
          img.onload = () => res();
          img.onerror = () => res(); // a missing frame must never hang the hero
        })),
      ).then(() => { lastFrame = -1; render(lastP, lastExit); });
    }

    const video = videoRef.current;
    if (mode === 'video' && video && videoSrc) {
      setLoading(true);
      const onReady = (): void => setLoading(false);
      video.addEventListener('loadeddata', onReady, { once: true });
      video.load();
    }

    let lastFrame = -1;
    let lastP = 0;
    let lastExit = 0;
    /* procedural dedupe — ticks with no visual change must not repaint 1920×1080.
       During the exit push-in `exit` varies, so the zoom stays a per-tick vector
       redraw (crisp); on desktop (fit===1) exit is a no-op but cheap. */
    let lastDrawnP = -1;
    let lastDrawnExit = -1;
    const render = (p: number, exit = 0): void => {
      lastP = p;
      lastExit = exit;
      if (mode === 'procedural') {
        if (p === lastDrawnP && exit === lastDrawnExit) return;
        lastDrawnP = p;
        lastDrawnExit = exit;
        drawProcedural(ctx, p, boxW, boxH, exit);
        return;
      }
      if (mode === 'frames') {
        const idx = Math.min(frameCount - 1, Math.floor(p * frameCount));
        if (idx === lastFrame) return; // dedupe — never repaint the same frame
        const img = images[idx];
        if (img && img.complete && img.naturalWidth > 0) {
          drawCover(ctx, img, img.naturalWidth, img.naturalHeight);
          lastFrame = idx;
        }
        return;
      }
      if (mode === 'video' && video && video.duration) {
        video.currentTime = p * video.duration;
        drawCover(ctx, video, video.videoWidth, video.videoHeight);
      }
    };

    /* text phases — direct DOM writes, zero React re-renders during scroll */
    let lastPhase: 'intro' | 'mid' | 'final' | null = null;
    const updateText = (p: number): void => {
      const p1 = phase1Ref.current;
      const p3 = phase3Ref.current;
      const hint = hintRef.current;
      if (p1) {
        const t = clamp01(p / 0.22);
        p1.style.opacity = String(1 - t);
        p1.style.transform = `translate(-50%, -50%) scale(${1 + t * 0.08})`;
        p1.style.visibility = t >= 1 ? 'hidden' : 'visible';
      }
      if (p3) {
        const t = clamp01((p - 0.68) / 0.14);
        const offset = Math.max(45, window.innerHeight * 0.04); // responsive rise
        p3.style.opacity = String(t);
        p3.style.transform = `translateY(${(1 - t) * offset}px)`;
        p3.style.pointerEvents = t > 0.85 ? 'auto' : 'none';
      }
      if (hint) hint.style.opacity = p < 0.05 ? '1' : '0';
      const phase = p < 0.22 ? 'intro' : p < 0.68 ? 'mid' : 'final';
      if (phase !== lastPhase) { lastPhase = phase; onPhase?.(phase); }
    };

    /* repaint with the active palette when the theme toggles */
    const onTheme = (): void => { lastFrame = -1; lastDrawnP = -1; render(lastP, lastExit); };
    window.addEventListener('helix:theme', onTheme);

    /* Re-measure the cover box on a real resize/rotation and repaint. lvh is
       stable under URL-bar animation, so the mobile bar-toggle "resize" events
       no-op here (measureBox returns false) — only orientation/window changes
       recompute the fit. ScrollTrigger's pin is untouched (ignoreMobileResize). */
    const onResize = (): void => { if (measureBox()) { lastFrame = -1; lastDrawnP = -1; render(lastP, lastExit); } };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    /* ── reduced motion: static final composition, no GSAP at all ── */
    if (reduce) {
      container.style.height = '100vh';
      render(1, 1); // the true END state — pushed-in, covered
      updateText(1);
      return () => {
        window.removeEventListener('helix:theme', onTheme);
        window.removeEventListener('resize', onResize);
        window.removeEventListener('orientationchange', onResize);
      };
    }

    let trigger: ScrollTriggerSelf | null = null;
    let disposed = false;
    let cancelled = false;
    (async () => {
      try {
        const { ScrollTrigger } = await loadGsap();
        if (cancelled) return;
        // phones: don't let the URL-bar show/hide re-measure the pin mid-scrub
        ScrollTrigger.config({ ignoreMobileResize: true });
        trigger = ScrollTrigger.create({
          trigger: container,
          start: 'top top',
          // The touch runway is CSS-sticky and never JS-pinned. Desktop retains the
          // original ScrollTrigger pin. Both use the container's reserved distance.
          end: () => `+=${Math.max(1, container.offsetHeight - wrapper.offsetHeight)}`,
          pin: touchCapable ? false : wrapper,
          // hard-linked everywhere: a numeric scrub was tried on touch and made the
          // animation lag the raw scroll at the UNPIN boundary — the wrapper released
          // while the canvas was still easing, which read as a clip (owner report
          // 2026-07-07). Desktop smoothness comes from Lenis lerping the scroll itself.
          scrub: true,
          anticipatePin: touchCapable ? 0 : 1,
          onUpdate: (self: ScrollTriggerSelf) => {
            // Storyboard plateau (Apple pattern): the sequence completes at
            // ANIMATION_END of the runway; clamp01 holds p=1 through the dwell, so
            // the settled final frame + CTAs stay on screen for the remaining scroll
            // distance and the render dedupe skips repaints while it holds. Scrolling
            // back up re-scrubs symmetrically. No locks, no direction logic.
            const p = clamp01(self.progress / ANIMATION_END);
            // Exit push-in rides the END of the dwell (raw 0.72→1): the final copy is
            // read on a still frame first, then the camera pushes in as the hero is
            // actually left. Keyed on RAW progress — the dwell, not the sequence.
            const exitT = seg(self.progress, 0.72, 1);
            render(p, exitT);
            updateText(p);
          },
        });
        ScrollTrigger.refresh();
        render(0);
        updateText(0);
      } catch {
        if (!disposed) {
          // CDN unreachable ⇒ static final frame, final copy visible (§6 rule 4)
          setFallback(true);
          container.style.height = '100vh';
          render(1, 1);
          updateText(1);
        }
      }
    })();

    return () => {
      cancelled = true;
      disposed = true;
      window.removeEventListener('helix:theme', onTheme);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      delete container.dataset.nativeTouch;
      trigger?.kill();
    };
  }, [mode, scrollHeight, framePathPattern, frameCount, frameStart, framePadLength, videoSrc, onPhase]);

  return (
    <section className="scroll-container" ref={containerRef} data-fallback={fallback || undefined}>
      <div className="animation-wrapper" ref={wrapperRef}>
        <canvas className="hero-canvas" ref={canvasRef} aria-hidden="true" />
        {mode === 'video' && videoSrc && (
          <video
            ref={videoRef}
            src={videoSrc}
            muted
            playsInline
            preload="auto"
            style={{ display: 'none' }}
          />
        )}
        {loading && (
          <div className="hero-loading-overlay" role="status" aria-live="polite">
            <div className="hero-spinner" />
            <span>PREPARING SEQUENCE…</span>
          </div>
        )}

        {/* Phase 1 — 0 → 22% */}
        <div className="hero-phase hero-phase-intro" ref={phase1Ref}>
          <div className="hero-aura" aria-hidden="true" />
          <p className="hero-eyebrow">{eyebrow}</p>
          <h1 className="hero-headline-intro">
            {headline} <span className="accent">{headlineAccent}</span>
          </h1>
          <p className="hero-intro-sub">{introSub}</p>
          <HeroPrompt />
        </div>

        {/* Phase 3 — 68 → 82% */}
        <div className="hero-phase hero-phase-final" ref={phase3Ref}>
          <h2 className="hero-headline-final">{finalHeadline}</h2>
          <p className="hero-subheadline-final">{finalSub}</p>
          <div className="hero-cta-row">
            <a className="hero-cta-button hero-cta-primary" href={ctaPrimary.href}>{ctaPrimary.label}</a>
            <a className="hero-cta-button hero-cta-secondary" href={ctaSecondary.href}>{ctaSecondary.label}</a>
          </div>
        </div>

        <div className="hero-scroll-hint" ref={hintRef} aria-hidden="true">
          <span>SCROLL</span>
          <span className="hero-bob">▾</span>
        </div>
      </div>
    </section>
  );
}
